//cspell:ignore suscribe Suscribe
import { useAtom } from 'jotai';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  Button,
  Flex,
  IconSvg,
  If,
  Input,
  InputTextArea,
  Select,
  Text,
  Tooltip,
} from '@/lib/v2/components';
import { Option } from '@/lib/v2/components/Select/Select';
import { DeleteIconNew } from '@/lib/v2/icons/outline';

import { CountWhatsApp } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/components/WhatsAppForm/components/CountWhatsApp';

import { useSuscribeWhatsAppTemplateForm } from './hooks/useSuscribeWhatsAppTemplateForm';
import { useWhatsAppActions } from './hooks/useWhatsAppActions';
import { useWhatsAppTemplateForm } from './hooks/useWhatsAppTemplateForm';

import { atomCurrentWhatsAppMessage } from '@/modules/CampaignsModule/atoms/actionWhatsAppAtom';
import { MAX_LENGTH_WHATSAPP } from '@/modules/CampaignsModule/components/actionManagement/WhatsAppAction/constants';
import { SubjectButtons } from '@/modules/CampaignsModule/components/SubjectButtons';
import {
  EButtonType,
  ISubjectChangeButtonsProps,
} from '@/modules/CampaignsModule/components/SubjectButtons/SubjectButtons.interface';
import { TRANSLATE_WHATSAPP_ACTION_FORM } from '@/modules/CampaignsModule/constants';

export const WhatsAppTemplateInputs = () => {
  const { t } = useTranslation('inbox');
  const { actionId } = useParams();

  const {
    control,
    RULES,
    setValue,
    getValues,
    watch,
    handleSubmitTemplate,
    handleDismissTemplate,
  } = useWhatsAppTemplateForm();

  useSuscribeWhatsAppTemplateForm({ watch });

  const [messageLength, setMessageLength] = useState(0);
  const [currentMessage, setCurrentMessage] = useAtom(atomCurrentWhatsAppMessage);

  const { actionsButton, handleChangeActionField, addActionButton, removeActionButton } =
    useWhatsAppActions({ setValue });

  useEffect(() => {
    setMessageLength(currentMessage.length);
  }, [currentMessage]);

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const transformedValue = event.target.value
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z\d_]/g, '');
      setValue('title', transformedValue);
    },
    [setValue]
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChangeMessage = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setCurrentMessage(event.target.value);
    },
    [setCurrentMessage]
  );

  const insertTextAtCursor = useCallback(
    (text: string) => {
      const textarea = textareaRef.current;
      const formValue = getValues('message') || '';

      if (!textarea) {
        const newMessage = formValue + text;
        setValue('message', newMessage);
        setCurrentMessage(newMessage);
        return;
      }

      const { selectionStart, selectionEnd, value } = textarea;
      const newMessage = value.substring(0, selectionStart) + text + value.substring(selectionEnd);

      setValue('message', newMessage);
      setCurrentMessage(newMessage);

      textarea.focus();
      const cursorPosition = selectionStart + text.length;
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
    },
    [getValues, setValue, setCurrentMessage]
  );

  const handleChangeSubjectButton = useCallback(
    ({ item }: ISubjectChangeButtonsProps) => {
      insertTextAtCursor(item);
    },
    [insertTextAtCursor]
  );

  const handleButtonTypeChange = useCallback(
    (index: number, value: Option | Option[]) => {
      if (value && !Array.isArray(value)) {
        handleChangeActionField(index, 'type', value.id.toString());
      }
    },
    [handleChangeActionField]
  );

  const handleButtonValueChange = useCallback(
    (index: number, value: string) => {
      handleChangeActionField(index, 'url', value);
    },
    [handleChangeActionField]
  );

  const handleButtonTitleChange = useCallback(
    (index: number, value: string) => {
      handleChangeActionField(index, 'title', value);
    },
    [handleChangeActionField]
  );

  const options: Option[] = [
    { id: '0', name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.headerOptions.noHeader`) },
    { id: 'text', name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.headerOptions.text`) },
    { id: 'media', name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.headerOptions.media`) },
  ];

  const buttonOptions: Option[] = [
    { id: 'URL', name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonTypeOptions.URL`) },
    {
      id: 'PHONE_NUMBER',
      name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonTypeOptions.PHONE_NUMBER`),
      disabled: true,
    },
    {
      id: 'VOICE_CALL',
      name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonTypeOptions.VOICE_CALL`),
      disabled: true,
    },
    {
      id: 'QUICK_REPLY',
      name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonTypeOptions.QUICK_REPLY`),
      disabled: true,
    },
    {
      id: 'COPY_CODE',
      name: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonTypeOptions.COPY_CODE`),
      disabled: true,
    },
  ];

  const headerSelect = watch('header_select');

  return (
    <div className="flex w-full flex-col gap-4 pr-4">
      <Input
        isRequired
        control={control}
        label={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.titleTemplate`)}
        name="title"
        placeHolder={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.titleTemplatePlaceholder`)}
        rules={RULES.title}
        type="text"
        onChange={handleTitleChange}
      />
      <Text className="-mt-3" color="light-black" fontWeight="normal" variant="text">
        {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.titleTemplateDescription`)}
      </Text>

      <div className="flex size-full gap-4">
        <div className="w-52">
          <Select
            isRequired
            control={control}
            label={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.selectHeaderTemplateLabel`)}
            name="header_select"
            options={options}
            placeholder={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.selectHeaderTemplatePlaceholder`)}
            rules={RULES.header_select}
          />
        </div>

        <If condition={headerSelect?.id === 'text'}>
          <div className="mt-4 flex-1">
            <Input
              isRequired
              control={control}
              name="header_text"
              placeholder={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.headerTextTemplatePlaceholder`)}
              rules={RULES.header_text}
            />
          </div>
        </If>

        <If condition={headerSelect?.id === 'media'}>
          <div className="mt-4 flex-1">
            <Input
              isRequired
              control={control}
              name="media_url"
              placeholder={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.mediaTemplatePlaceholder`)}
              rules={RULES.media_url}
            />
            <Text color="light-black" fontWeight="normal" variant="text">
              {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.addImageTooltip`)}
            </Text>
          </div>
        </If>
      </div>

      <div className="-mt-1 w-full">
        <div className="flex gap-4">
          <div className="flex-1">
            <InputTextArea
              ref={textareaRef}
              isRequired
              control={control}
              id="whatsapp_message"
              label={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.bodyMessageLabel`)}
              name="message"
              rows={7}
              rules={RULES.message}
              onChange={handleChangeMessage}
            />
            <div className="flex justify-end">
              <CountWhatsApp
                count={messageLength}
                length={messageLength}
                maxLength={MAX_LENGTH_WHATSAPP}
              />
            </div>
          </div>

          <div className="mt-5 w-fit">
            <SubjectButtons
              vertical
              actionId={Number(actionId)}
              exclude={[EButtonType.TAG]}
              id="whatsapp_form-buttons"
              searchPlaceholder={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.searchDropdown`)}
              onChangeButtons={handleChangeSubjectButton}
            />
          </div>
        </div>
      </div>

      <Input
        control={control}
        label={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.footerTemplateTitle`)}
        name="footer"
        placeHolder={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.footerTemplateTitlePlaceholder`)}
        rules={RULES.footer}
        type="text"
      />

      {actionsButton.map((button, index) => (
        <div key={button.id} className="h-full rounded-lg border border-emblue-borders">
          <div className="flex items-center justify-between gap-4 border-b border-b-emblue-borders px-5 py-2 align-middle">
            <Text fontWeight="medium" variant="text">
              {button.name}
            </Text>
            <Button link onClick={() => removeActionButton(button.id)}>
              <IconSvg
                fillColor="destructive"
                height="20px"
                svgComponent={<DeleteIconNew />}
                width="20px"
              />
            </Button>
          </div>

          <div className="flex flex-col gap-4 px-5 py-3">
            <div className="flex size-full gap-4">
              <div className="w-64">
                <Select
                  isRequired
                  control={control}
                  label={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.selectButtonTypeLabel`)}
                  name={`button_type_${button.id}`}
                  options={buttonOptions}
                  placeholder={undefined}
                  rules={RULES.select_button_type}
                  onChange={(value: Option | Option[]) => handleButtonTypeChange(index, value)}
                />
              </div>

              <div className="flex-1">
                <Input
                  isRequired
                  control={control}
                  label={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonTitleTemplateLabel`)}
                  name={`button_url_${button.id}`}
                  placeholder={t(
                    `${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonTitleTemplatePlaceholder`
                  )}
                  rules={RULES.button_title}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleButtonTitleChange(index, e.target.value)
                  }
                />
              </div>
            </div>
            <Input
              isRequired
              control={control}
              label={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonUrlTemplateLabel`)}
              name={`button_text_${button.id}`}
              placeHolder={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.buttonUrlTemplatePlaceholder`)}
              rules={RULES.button_url}
              type="text"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleButtonValueChange(index, e.target.value)
              }
            />
          </div>
        </div>
      ))}

      <div className="flex justify-start">
        <If condition={actionsButton.length < 2}>
          <Button outline secondary onClick={addActionButton}>
            {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.addActionButton`)}
          </Button>
        </If>
        <If condition={actionsButton.length >= 2}>
          <Tooltip
            className="whitespace-nowrap"
            content={t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.maxActionsReached`)}
            position="right"
          >
            <Button disabled outline secondary onClick={addActionButton}>
              {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.addActionButton`)}
            </Button>
          </Tooltip>
        </If>
      </div>

      <Flex withGap alignment="end" gapSize="small" itemAlignment="center">
        <Button outline secondary id="cancel-button" size="small" onClick={handleDismissTemplate}>
          {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.cancel`)}
        </Button>

        <Button primary id="validate-button" size="small" onClick={handleSubmitTemplate}>
          {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.validate`)}
        </Button>
      </Flex>
    </div>
  );
};

export default WhatsAppTemplateInputs;
